import { useEffect } from "react";
import type { AiAgentDomain, PageContext, PageMetric } from "@/services/aiAgentApi";
import { fetchHealthcareDashboard, type HealthcareDashboard } from "@/services/healthcareApi";
import {
  fetchLaborDashboard,
  LABOR_SEGMENTS,
  type LaborDashboard,
  type LaborPeriod,
  type LaborSegment,
} from "@/services/laborApi";
import {
  fetchSocialProtectionDashboard,
  type SocialProtectionDashboard,
  type SocialProtectionPeriod,
} from "@/services/socialProtectionApi";
import {
  fetchPublicSafetyDashboard,
  type PublicSafetyDashboard,
  type PublicSafetyPeriod,
} from "@/services/publicSafetyApi";
import {
  fetchBusinessInvestorsAkimDashboard,
  type BusinessInvestorsAkimDashboard,
} from "@/services/businessInvestorsAkimService";
import { ATYRAU_ALL } from "@/constants/atyrauDistricts";
import { fetchScheduleEvents } from "@/services/scheduleApi";
import { toIsoDate } from "@/utils/scheduleDates";

/*
 * Срез экрана для ИИ-агента в режиме `page`.
 *
 * ЗАЧЕМ. У сервиса MAI есть свои таблицы только по обращениям, демографии и
 * образованию. Бизнес, труд, здравоохранение, соцзащита и безопасность стоят на
 * ручках нашего бэкенда, которых сервис не видит. Экран их уже запросил,
 * посчитал и подписал — режим `page` берёт этот готовый срез и отвечает по
 * нему, ничего не досчитывая.
 *
 * СРЕЗ ОТДАЁТ САМ МОДУЛЬ (`useAiPageContext`), потому что только он знает
 * выбранный период, район и сегмент. Ассистент читает срез В МОМЕНТ ОТПРАВКИ:
 * человек мог сменить район между вопросами, и третий вопрос обязан уйти с
 * числами третьего экрана.
 *
 * Если раздел не открыт (быстрая команда с главной), срез собирается теми же
 * функциями из того же кэша экрана с фильтрами по умолчанию — числа совпадут с
 * тем, что человек увидит, открыв раздел.
 */

const nf = (n: number) => n.toLocaleString("ru-RU");

const PERIOD_LABEL: Record<LaborPeriod | SocialProtectionPeriod | PublicSafetyPeriod, string> = {
  week: "Неделя",
  month: "Месяц",
  quarter: "Квартал",
};

/** `YYYY-MM-DD…` или `ДД.ММ.ГГГГ` → `YYYY-MM-DD`; иначе пусто — сервис ждёт ровно этот вид. */
function isoDay(s: string | null | undefined): string | undefined {
  if (!s) return undefined;
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  if (iso) return iso[1];
  const ru = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(s);
  return ru ? `${ru[3]}-${ru[2]}-${ru[1]}` : undefined;
}

/*
 * KPI с прошлым периодом — в подсказку, а не отдельной метрикой: агенту нужна
 * пара «сейчас / тогда», и подписанная рядом она не перепутается с соседней.
 */
function kpiMetric(
  prefix: string,
  k: { id: string; label: string; value: number | null; previous?: number | null; hint?: string | null },
  unit?: string,
): PageMetric | null {
  if (k.value == null) return null;
  const parts = [k.hint, k.previous != null ? `прошлый период: ${nf(k.previous)}` : null].filter(
    Boolean,
  );
  return {
    key: `${prefix}_${k.id}`,
    label: k.label,
    value: k.value,
    unit,
    display: nf(k.value),
    ...(parts.length ? { hint: parts.join("; ") } : {}),
  };
}

const metrics = (items: (PageMetric | null)[]) => items.filter((m): m is PageMetric => m !== null);

const named = (rows: { name: string; value: number }[]) =>
  rows.map((r) => ({ label: r.name, value: r.value }));

/* ── Разделы ─────────────────────────────────────────────────────────── */

export function healthcarePageContext(d: HealthcareDashboard): PageContext {
  /*
   * ДАТА ИЗ БУДУЩЕГО НЕ УХОДИТ В `as_of`. В выгрузке РПН есть записи с датами
   * до 2027 года (ошибки ввода), и «самая поздняя дата» сводки оказывается
   * впереди сегодняшней — агент назвал бы срез «данными на 14.02.2027».
   */
  const asOf = isoDay(d.asOf);
  const futureAsOf = !!asOf && asOf > new Date().toISOString().slice(0, 10);
  return {
    context_version: 1,
    page: { id: "healthcare", title: "Здравоохранение", url: "/healthcare", locale: "ru" },
    provenance: {
      kind: "live",
      sources: ["РПН — регистр прикреплённого населения"],
      ...(asOf && !futureAsOf ? { as_of: asOf } : {}),
    },
    metrics: metrics([...d.kpis, ...d.networkKpis].map((k) => kpiMetric("health", k))),
    breakdowns: [
      { key: "age_bands", label: "Возраст регистра", dimension: "Возраст", unit: "человек", rows: named(d.ageBands) },
      { key: "sex", label: "Пол регистра", dimension: "Пол", unit: "человек", rows: named(d.sex) },
      // Экран показывает верхушку разрезов — сумма строк не итог.
      { key: "death_causes", label: "Смертность по причине", dimension: "Причина", unit: "смертей", truncated: true, rows: named(d.deathCauses) },
      { key: "birth_places", label: "Место родов", dimension: "Место", unit: "рождений", truncated: true, rows: named(d.birthPlaces) },
    ],
    series: [
      { key: "births_by_month", label: "Рождения по месяцам", unit: "человек", points: d.dynamics.map((p) => ({ t: p.label, value: p.births })) },
      { key: "deaths_by_month", label: "Смерти по месяцам", unit: "человек", points: d.dynamics.map((p) => ({ t: p.label, value: p.deaths })) },
    ],
    notes: [
      d.sourceNote,
      ...(d.incomplete ? ["Ряд по месяцам неполный: выгрузка упёрлась в потолок страниц."] : []),
      ...(futureAsOf
        ? [`В выгрузке есть даты позже сегодняшней (до ${asOf}) — это ошибки ввода, дата среза неизвестна.`]
        : []),
    ],
    cannot_answer: [
      "Разрез по районам: района в РПН нет.",
      "Диагнозы и заболеваемость: в РПН их нет.",
    ],
  };
}

export function laborPageContext(
  d: LaborDashboard,
  f: { period: LaborPeriod; segment: LaborSegment; district: string },
): PageContext {
  const segment = LABOR_SEGMENTS.find((s) => s.value === f.segment)?.label ?? f.segment;
  return {
    context_version: 1,
    page: { id: "labor", title: "Труд и занятость", url: "/labor", locale: "ru" },
    provenance: { kind: "live", sources: ["ВЛИ — работодатели"], as_of: isoDay(d.extractDate) },
    filters: [
      { label: "Территория", value: f.district || d.territory },
      { label: "Период сравнения", value: PERIOD_LABEL[f.period] },
      { label: "Сегмент", value: segment },
    ],
    metrics: metrics([...d.kpis, ...d.complianceKpis].map((k) => kpiMetric("labor", k))),
    breakdowns: [
      { key: "size_structure", label: "Работодатели по размеру", dimension: "Размер", rows: named(d.sizeStructure) },
      { key: "compliance", label: "Нарушения работодателей", dimension: "Нарушение", rows: named(d.complianceBreakdown) },
      { key: "pay_by_size", label: "Невыплаты по размеру", dimension: "Размер", rows: named(d.payBySize) },
      {
        key: "employers_by_district",
        label: "Работодатели по районам",
        dimension: "Район",
        rows: d.districts.map((r) => ({ label: r.districtName, value: r.employers })),
      },
    ],
    series: [
      { key: "employers", label: "Работодатели по срезам", points: d.dynamics.map((p) => ({ t: p.label, value: p.employers })) },
      { key: "pay_remain", label: "Работодатели с остатком невыплат", points: d.dynamics.map((p) => ({ t: p.label, value: p.payRemainEmployers })) },
    ],
    tables: [
      {
        key: "districts",
        label: "Районы",
        columns: ["Район", "Работодателей", "С остатком невыплат", "С остатком ЕХР", "Без ОСНС"],
        rows: d.districts.map((r) => [r.districtName, r.employers, r.payRemainEmployers, r.ehrRemainEmployers, r.osns]),
      },
    ],
    notes: [d.sourceNote],
  };
}

export function socialProtectionPageContext(
  d: SocialProtectionDashboard,
  f: { period: SocialProtectionPeriod; district: string },
): PageContext {
  return {
    context_version: 1,
    page: { id: "social_protection", title: "Социальная защита", url: "/social-protection", locale: "ru" },
    provenance: { kind: "live", sources: ["ГБД ФЛ", "Реестр рисков"], as_of: isoDay(d.asOf) },
    filters: [
      { label: "Территория", value: f.district || d.territory },
      { label: "Период сравнения", value: PERIOD_LABEL[f.period] },
    ],
    metrics: metrics(d.kpis.map((k) => kpiMetric("social", k, "человек"))),
    breakdowns: [
      { key: "categories", label: "Категории", dimension: "Категория", unit: "человек", rows: named(d.categories) },
      { key: "risk_levels", label: "Уровни риска", dimension: "Уровень", unit: "человек", rows: named(d.riskLevels) },
    ],
    series: [
      { key: "pensioners", label: "Пенсионеры по срезам", unit: "человек", points: d.dynamics.map((p) => ({ t: p.label, value: p.pensioners })) },
    ],
    tables: [
      {
        key: "districts",
        label: "Районы",
        columns: ["Район", "Население", "Пенсионеры", "Неполные семьи", "Сироты", "ОБПР", "Критичный риск", "Высокий риск"],
        rows: d.districts.map((r) => [
          r.districtName,
          r.population,
          r.pensioners,
          r.notFullFamily,
          r.orphans,
          r.obpr,
          r.criticalRisk,
          r.highRisk,
        ]),
      },
    ],
    notes: [d.sourceNote],
    cannot_answer: ["Персональные данные: имена, ИИН, адреса."],
  };
}

export function publicSafetyPageContext(
  d: PublicSafetyDashboard,
  f: { period: PublicSafetyPeriod; district: string },
): PageContext {
  const districtRows = (rows: PublicSafetyDashboard["kui"]["districts"]) =>
    rows.map((r) => ({ label: r.name, value: r.total }));
  return {
    context_version: 1,
    page: { id: "public_safety", title: "Общественная безопасность", url: "/public-safety", locale: "ru" },
    provenance: { kind: "live", sources: ["КУИ — книга учёта информации", "ЕРАП — административные протоколы"] },
    filters: [
      { label: "Территория", value: f.district || d.district?.name || "Атырауская область" },
      { label: "Период", value: PERIOD_LABEL[f.period] },
    ],
    metrics: metrics(d.kpis.map((k) => kpiMetric("safety", k))),
    breakdowns: [
      { key: "kui_by_type", label: "Сообщения КУИ по видам", dimension: "Вид", unit: "сообщений", rows: d.kui.byType.map((r) => ({ label: r.name, value: r.value, ...(r.share != null ? { share: r.share } : {}) })) },
      { key: "kui_by_district", label: "Сообщения КУИ по районам", dimension: "Район", unit: "сообщений", rows: districtRows(d.kui.districts) },
      { key: "admin_top_articles", label: "Админ. нарушения: топ статей КоАП", dimension: "Статья", unit: "протоколов", truncated: true, rows: d.admin.topArticles.map((r) => ({ label: r.name, value: r.value })) },
      { key: "admin_by_district", label: "Админ. нарушения по районам", dimension: "Район", unit: "протоколов", rows: districtRows(d.admin.districts) },
    ],
    series: [
      { key: "kui_dynamics", label: "Сообщения КУИ по месяцам", unit: "сообщений", points: d.kui.dynamics.map((p) => ({ t: p.label, value: p.value })) },
    ],
    notes: [d.sourceNote, `Админ. нарушения: ${d.admin.periodLabel}.`],
    cannot_answer: ["Помесячная динамика административных нарушений: у ЕРАП её нет."],
  };
}

export function businessPageContext(d: BusinessInvestorsAkimDashboard, district: string): PageContext {
  const rows = (items: BusinessInvestorsAkimDashboard["byIndustry"]) =>
    items.map((r) => ({ label: r.name, value: r.value }));
  return {
    context_version: 1,
    page: { id: "business_investors", title: "Бизнес и инвесторы", url: "/business-investors", locale: "ru" },
    provenance: { kind: "live", sources: ["НЦИП — инвестиционные проекты"] },
    filters: [{ label: "Территория", value: district === ATYRAU_ALL ? d.territoryLabel : district }],
    /*
     * KPI бизнеса приходят уже отформатированными строками («1,2 трлн ₸»):
     * разбирать их обратно в число — гадать о единицах. Поэтому сводка идёт
     * записью (`entity`), где значение может быть строкой как есть.
     */
    entity: {
      kind: "Сводка",
      title: d.territoryLabel,
      fields: d.kpis.map((k) => ({
        label: k.label,
        value: k.value,
        ...(k.hint ? { unit: k.hint } : {}),
      })),
    },
    breakdowns: [
      { key: "by_industry", label: "Проекты по отраслям", dimension: "Отрасль", unit: "проектов", rows: rows(d.byIndustry) },
      { key: "by_realization", label: "Ход реализации", dimension: "Статус", unit: "проектов", rows: rows(d.byRealization) },
      { key: "by_district", label: "Проекты по районам", dimension: "Район", unit: "проектов", rows: rows(d.byDistrict) },
      { key: "by_country", label: "Проекты по странам инвесторов", dimension: "Страна", unit: "проектов", rows: rows(d.byCountry) },
    ],
    tables: [
      {
        key: "top_projects",
        label: "Крупнейшие проекты",
        columns: ["Проект", "Район", "Отрасль", "Стоимость, млн ₸", "Рабочих мест", "Ход реализации"],
        rows: d.topProjects.map((p) => [p.name, p.district, p.industry, p.costMlnKzt, p.jobs, p.realizationStatus]),
      },
    ],
    notes: [d.sourceNote],
    cannot_answer: ["Проверки бизнеса и жалобы предпринимателей: в НЦИП их нет."],
  };
}

/** Личный график на день — тот же, что видит пользователь на `/schedule`. */
export async function schedulePageContext(date: Date, title: string): Promise<PageContext> {
  const iso = toIsoDate(date);
  const events = await fetchScheduleEvents(iso, iso);
  const day = date.toLocaleDateString("ru-RU");
  return {
    context_version: 1,
    page: { id: "schedule", title, url: "/schedule", locale: "ru" },
    provenance: { kind: "live", sources: ["Мой график"], as_of: iso },
    metrics: [{ key: "events", label: `Событий на ${day}`, value: events.length, display: nf(events.length) }],
    tables: [
      {
        key: "events",
        label: `График на ${day}`,
        columns: ["Начало", "Окончание", "Событие", "Место", "Важное"],
        rows: events.map((e) => [
          e.startTime,
          e.endTime,
          e.title.replace(/\n/g, " "),
          e.location,
          e.important ? "да" : null,
        ]),
      },
    ],
    notes: ["Свободное время — промежутки между событиями в рабочий день 09:00–18:00."],
  };
}

/* ── Срез текущего экрана ────────────────────────────────────────────── */

let current: PageContext | null = null;

/**
 * Модуль сообщает свой срез. `null` — данных ещё нет (или ошибка): вопрос тогда
 * уйдёт со срезом по умолчанию, а не с пустым — пустой сервис отвергает (422).
 */
export function useAiPageContext(ctx: PageContext | null): void {
  useEffect(() => {
    current = ctx;
    return () => {
      if (current === ctx) current = null;
    };
  }, [ctx]);
}

/** Срез экрана с данным id, если именно он сейчас на экране. */
function currentFor(pageId: string): PageContext | null {
  return current?.page.id === pageId ? current : null;
}

/* ── Куда идёт вопрос ────────────────────────────────────────────────── */

export type AiRoute =
  | { domain: Exclude<AiAgentDomain, "page"> }
  | { domain: "page"; load: () => Promise<PageContext> };

/*
 * Срезы по умолчанию — ровно те фильтры, с которыми раздел открывается. Данные
 * берутся из того же часового кэша экрана (`cachedCall`), поэтому повторный
 * вопрос запросов не шлёт.
 */
const SECTION_LOADERS: Record<string, { pageId: string; load: () => Promise<PageContext> }> = {
  healthcare: {
    pageId: "healthcare",
    load: async () => healthcarePageContext(await fetchHealthcareDashboard()),
  },
  labor: {
    pageId: "labor",
    load: async () =>
      laborPageContext(await fetchLaborDashboard({ unit: "month", segment: "ul" }), {
        period: "month",
        segment: "ul",
        district: "",
      }),
  },
  "social-protection": {
    pageId: "social_protection",
    load: async () =>
      socialProtectionPageContext(await fetchSocialProtectionDashboard({ unit: "month" }), {
        period: "month",
        district: "",
      }),
  },
  "public-safety": {
    pageId: "public_safety",
    load: async () =>
      publicSafetyPageContext(await fetchPublicSafetyDashboard({ unit: "month" }), {
        period: "month",
        district: "",
      }),
  },
  "business-investors": {
    pageId: "business_investors",
    load: async () =>
      businessPageContext(await fetchBusinessInvestorsAkimDashboard({ district: ATYRAU_ALL }), ATYRAU_ALL),
  },
};

/** Срез раздела: тот, что на экране, — или собранный с фильтрами по умолчанию. */
export function sectionRoute(section: string): AiRoute | null {
  const entry = SECTION_LOADERS[section];
  if (!entry) return null;
  return { domain: "page", load: async () => currentFor(entry.pageId) ?? (await entry.load()) };
}

/*
 * ТЕМА ВОПРОСА — для экранов без своего домена (главная, каталог модулей,
 * преступность…). Раньше отсюда всё уходило в `otinish`, и «какая статистика
 * по населению» с главной получала отказ агента обращений: «демография в этом
 * чате недоступна» (01.10.2026). Порядок важен: сроки и решения органов
 * проверяются раньше обращений вообще, иначе «обращения» перехватили бы их.
 *
 * Слова — основы, без окончаний: «населени» ловит «население» и «населению».
 * «Жители» сюда не входят: «трудности у жителей с водой» — вопрос к обращениям.
 * Не узнали тему — обращения: самый широкий корпус у сервиса.
 */
const TOPICS: { words: string[]; route: () => AiRoute | null }[] = [
  {
    words: ["населени", "демограф", "рожда", "рождени", "смертност", "умерл", "брак", "развод", "возраст", "перепис"],
    route: () => ({ domain: "population" }),
  },
  { words: ["вуз", "студент", "университет", "колледж", "высшее образ", "образовательн программ"], route: () => ({ domain: "epvo" }) },
  // «органы», а не «орган»: основа сидит внутри «медорганизаций».
  { words: ["срок", "просроч", "органы", "органов", "госорган", "отписк"], route: () => ({ domain: "control" }) },
  { words: ["закуп", "тендер", "медиан", "завышен"], route: () => ({ domain: "procurement" }) },
  { words: ["здравоохр", "больниц", "медорганизац", "поликлиник", "рпн"], route: () => sectionRoute("healthcare") },
  // «трудов…», а не «труд»: основа ловит «трудности».
  { words: ["трудов", "трудоустр", "труд и занят", "работодат", "зарплат", "невыплат", "занятост"], route: () => sectionRoute("labor") },
  { words: ["пенсион", "сирот", "соцзащит", "социальн защит", "многодет", "уязвим"], route: () => sectionRoute("social-protection") },
  { words: ["правопоряд", "безопасност", "куи", "правонаруш", "коап", "протокол"], route: () => sectionRoute("public-safety") },
  { words: ["инвест", "нцип", "бизнес"], route: () => sectionRoute("business-investors") },
];

function topicRoute(question: string): AiRoute | null {
  const q = question.toLowerCase();
  for (const t of TOPICS) {
    if (t.words.some((w) => q.includes(w))) return t.route();
  }
  return null;
}

/*
 * ДОМЕН ПО РАЗДЕЛУ. Обращения, население и образование — свои таблицы у
 * сервиса, агент считает сам. Пять разделов на ручках нашего бэкенда — `page`.
 * На экране раздела вопрос идёт туда, даже если в нём есть чужие слова:
 * «рождения» на «Здравоохранении» — про РПН этого экрана, а не про ЗАГС.
 *
 * ЭКРАН БЕЗ СВОЕГО ДОМЕНА — по теме вопроса (`topicRoute`), иначе обращения.
 */
export function aiRouteForSection(section: string, question = ""): AiRoute {
  if (section === "appeals" || section === "citizens-messages") return { domain: "otinish" };
  if (section === "population" || section.startsWith("social-demographic")) return { domain: "population" };
  if (section === "education") return { domain: "epvo" };
  return sectionRoute(section) ?? topicRoute(question) ?? { domain: "otinish" };
}

/* ── Сводка по всем разделам ─────────────────────────────────────────── */

const SUMMARY_PER_SECTION = 7;

/**
 * Краткая сводка: KPI всех пяти разделов в одном срезе.
 *
 * Раздел, который не загрузился, НЕ замалчивается: он уходит в `cannot_answer`,
 * иначе агент назвал бы сводку из трёх разделов сводкой «по всем направлениям».
 */
export async function summaryPageContext(): Promise<PageContext> {
  const entries = Object.entries(SECTION_LOADERS);
  const settled = await Promise.allSettled(entries.map(([, e]) => e.load()));
  const parts: PageContext[] = [];
  const missing: string[] = [];
  settled.forEach((r, i) => {
    if (r.status === "fulfilled") parts.push(r.value);
    else missing.push(entries[i][0]);
  });

  const titleOf: Record<string, string> = {
    healthcare: "Здравоохранение",
    labor: "Труд и занятость",
    "social-protection": "Социальная защита",
    "public-safety": "Общественная безопасность",
    "business-investors": "Бизнес и инвесторы",
  };

  return {
    context_version: 1,
    page: { id: "summary", title: "Краткая сводка по разделам", url: "/base", locale: "ru" },
    provenance: { kind: "live", sources: parts.flatMap((p) => p.provenance.sources ?? []) },
    metrics: parts.flatMap((p) =>
      [
        ...(p.metrics ?? []),
        /*
         * У бизнеса сводка — строками; в метрики идёт то, что число. Ключ — по
         * номеру поля, не по подписи: из русской подписи нормализация ключа
         * оставляет одни подчёркивания, и ключи двух полей совпадали.
         */
        ...(p.entity?.fields ?? []).flatMap((f, i) =>
          typeof f.value === "number"
            ? [{ key: `field_${i}`, label: f.label, value: f.value, display: nf(f.value) }]
            : [],
        ),
      ]
        .slice(0, SUMMARY_PER_SECTION)
        .map((m) => ({ ...m, key: `${p.page.id}_${m.key}`, label: `${p.page.title}: ${m.label}` })),
    ),
    notes: [
      "Обращения, население и образование в эту сводку не входят — по ним агент считает сам, спросите отдельно.",
    ],
    cannot_answer: missing.map((s) => `Раздел «${titleOf[s] ?? s}»: данные не загрузились.`),
  };
}
